<template lang="html">
  <section
    class="datepicker-dpe"
    :id="id"
    :draggable="draggable"
    @dragstart.stop="onComponentStartDrag($event)"
    @dragend.stop="onComponentEndDrag($event)"
    @dragover.prevent.stop
    @dragenter.stop="onComponentDragEnter($event)"
  >
    <div class="m-header">
      <div class="title">{{ $t("datepicker") }}</div>
      <settings-element
        @on-settings-edit-click="onSettingsClick"
        @on-settings-delete-click="onDeleteClick"
      />
    </div>

    <div class="card-description normal-wrap">
      {{ description }}
    </div>

    <div class="meta-data">
      <div class="meta-row">
        <div class="label">
          {{ $t("datepickerData") }}
        </div>
        <div class="value" :title="selectedDatepickerData">
          {{ selectedDatepickerData }}
        </div>
      </div>
    </div>

    <simple-modal
      :show="displaySimpleModal"
      :message="getMessageText(messages.ARE_YOU_SURE)"
      :cancel-text="getMessageText(messages.CANCEL)"
      :ok-text="getMessageText(messages.OK)"
      @on-simple-modal-cancel="onCancelDeleteElement"
      @on-simple-modal-ok="onConfirmDeleteElement"
    />
  </section>
</template>

<script lang="js">
  import store from "@/store";
  import { MUTATION_NAME } from "@/static/global-variables";
  import dragAndDrop from "@/views/drag-drop-pages/components/utils/dragAndDrop";
  import SimpleModal from "@/views/drag-drop-pages/components/modal/SimpleModal";
  import SettingsElement from "@/views/drag-drop-pages/components/SettingsElement";
  import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";

  export default  {
    name: "datepicker",
    components: {
      SimpleModal,
      SettingsElement
    },
    props: {
      id: {
        type: String
      },
      // props specific to draggable component
      allowDropzones: {
        type: Boolean,
        default: true
      },
      dropEffect: {
        type: String,
        default: dragAndDrop.DROP_EFFECT.MOVE
      },
      draggable: {
        default: true
      },
      // props specific to this component below
      data: {
        type: Object
      },
      description: {
        type: String
      }
    },
    beforeDestroy() {
      const info = {
        appId: this.appId,
        dataElementId: this.state,
        fromPageDelete: true
      }
      store.commit(MUTATION_NAME.EDIT_DROPDOWN_REFERENCE, info);
    },

    mounted () {
      this.setData(this.data);
    },
    data () {
      return {
        state: null,
        displaySimpleModal: false
      }
    },
    computed: {
      appId() {
        return dragAndDrop.getAppId();
      },
      appContent() {
        return JSON.parse(JSON.stringify(expressionBuilder.getAppContent(this.appId)));
      },
      messages() {
        return store.state.messages;
      },
      selectedDatepickerData() {
        return this.state && this.appContent.dataElements[this.state]
          ? this.appContent.dataElements[this.state].name
          : "";
      },
      deleteModal: {
        set(data) {
          store.dispatch(MUTATION_NAME.SET_DELETE_DRAGGABLE, data);
        },
        get() {
          return store.getters.deleteDraggableItem;
        }
      },
      editModal: {
        set(data) {
          store.dispatch(MUTATION_NAME.SET_EDIT_DRAGGABLE, data);
        },
        get() {
          return store.getters.editDraggableItem;
        }
      }
    },
    watch: {
      data: {
        deep: true,
        handler() {
          this.setData(this.data);
        }
      }
    },
    methods: {
      setData(data) {
        if(data) {
          this.type = data.type;
          this.label = data.label;
          this.state = data.state;
        }
      },

      onCancelDeleteElement() {
        this.displaySimpleModal = false;
      },
      onConfirmDeleteElement() {
        this.displaySimpleModal = false;
        const obj = {
          id: this.id
        };
        this.deleteModal = {detail: obj};
      },
      onDeleteClick() {
        this.displaySimpleModal = true;
      },

      onSettingsClick() {
        const obj = {
          id: this.id,
          modal: "AddDatepickerModal",
          dropEffect: dragAndDrop.DROP_EFFECT.NONE,
          props: {
            description: this.description,
            label: this.label,
            state: this.state
          }
        };
        this.editModal = {detail: obj};
      },
      /**
       * @function getMetadata
       * @returns {Object} Metadata in JSON format
       */
      getMetadata() {
        // meta must contain meta.element object with at least meta.element.name, do not remove
        return {
          element: {
            name: this.$options.name // this field is required for all components
          },
          // add elements required for this component
          type: "Date Picker",
          description: this.description,
          label: this.label,
          state: this.state
        };
      },
      /**
       * @function deserialize
       * @param {Object} serializedJson - Metadata to create content of this component
       */
      deserialize(serializedJson) {
        this.setData({
          label: serializedJson.label,
          state: serializedJson.state,
          type: serializedJson.type
        });
        this.description = serializedJson.description;
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "@/styles/components/main";
  @include drag-drop-a-tag;
  .datepicker-dpe {
    flex: 1;
    min-width: 150px;
    width: 100%;
    position: relative; /* component must be position relative */
    @include mixin-box;
    padding: 0.75rem;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: var(--DEFAULT_TEXT_BLACK);
  }
  .m-shrink {
    @include mixin-shrink-layout;
  }

  .m-header {
    display: grid;
    box-sizing: border-box;
    grid-template-columns: auto 3rem;
    .title {
      font-weight: bold;
      font-size: 0.9rem;
    }
  }

  .meta-data {
    display: flex;
    flex-direction: column;
    margin-bottom: 5px;
    .meta-row {
      padding-left: 0.25rem;
      padding-right: 0.25rem;
      margin-bottom: 0.5rem;

      .label,
      .value {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
      .value {
        font-weight: bold;
        font-size: 1rem;
      }
    }
  }
</style>
